import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hero from '../components/Hero';
import PropertyCard from '../components/PropertyCard';
import PropertyCategories from '../components/PropertyCategories';
import TanaviHighlights from '../components/TanaviHighlights';
import WhyChoose from '../components/WhyChoose';
import ChoiceProperties from '../components/ChoiceProperties';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import Essentials from '../components/Essentials';
import RegisterCTA from '../components/RegisterCTA';
import AnnouncementMarquee from '../components/AnnouncementMarquee';
import API_URL, { fetchWithTimeout } from '../utils/api';
import { readCache, writeCache, clearCache } from '../utils/propertyCache';

// ─── cache key ────────────────────────────────────────────────────────────────
const CACHE_KEY = 'featured';

function filterFeatured(data) {
  return data.filter(p => {
    const sections = p.sections || [p.section];
    const notExpired = !p.expiryDate || new Date(p.expiryDate) > new Date();
    return sections.includes('featured') && p.status === 'available' && notExpired && p.isActive !== false;
  });
}

// ─── component ────────────────────────────────────────────────────────────────
const Home = () => {
  const cachedData = readCache(CACHE_KEY);

  // Check if we have a scroll target waiting (set by any card/tile click)
  const hasPendingRestore = !!(sessionStorage.getItem('restoreScrollId') || sessionStorage.getItem('homeRestoreId'));

  // Seed from cache — cards render instantly on back-nav, no skeleton shown.
  const [properties, setProperties] = useState(cachedData || []);
  const [loading, setLoading]       = useState(!cachedData);
  const [searchQuery, setSearchQuery] = useState('');

  // Pre-mark all animated sections visible when we have a pending restore,
  // so entrance animations don't hide cards while scrollIntoView fires.
  const [visibleSections, setVisibleSections] = useState(
    hasPendingRestore
      ? { properties: true, 'tanavi-highlights': true, banner: true, 'agri-banner': true }
      : {}
  );

  // Separate ref maps — property cards and category tiles must NOT share keys
  const propertyRefs = useRef({});
  const categoryRefs = useRef({});
  const choiceRefs   = useRef({});
  const isRestoringRef = useRef(false);

  // ── ref helpers ─────────────────────────────────────────────────────────────
  // Use composite key (section:id) to handle same property in multiple sections
  const getPropertyRef = useCallback((id, section = 'properties') => {
    const key = `${section}:${id}`;
    if (!propertyRefs.current[key]) propertyRefs.current[key] = React.createRef();
    return propertyRefs.current[key];
  }, []);

  const getCategoryRef = useCallback((slug) => {
    if (!categoryRefs.current[slug]) categoryRefs.current[slug] = React.createRef();
    return categoryRefs.current[slug];
  }, []);

  const getChoiceRef = useCallback((slug) => {
    if (!choiceRefs.current[slug]) choiceRefs.current[slug] = React.createRef();
    return choiceRefs.current[slug];
  }, []);

  // ── data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchProperties = async (silent = false) => {
      try {
        const res  = await fetchWithTimeout(`${API_URL}/api/properties`, {}, 30000);
        const data = await res.json();
        if (cancelled) return;
        const filtered = filterFeatured(data);
        writeCache(CACHE_KEY, filtered);
        setProperties(filtered);
        if (!silent) setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching properties:', err);
        if (!silent) setLoading(false);
      }
    };

    // If cache is warm, do a silent background refresh — no loading flash.
    fetchProperties(!!cachedData);

    // Refresh every 10 s silently for faster admin updates
    const interval = setInterval(() => fetchProperties(true), 10_000);
    
    // Also refresh when page becomes visible (user switches back to tab)
    // Clear cache first to ensure deleted properties are fetched
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        clearCache(CACHE_KEY);
        fetchProperties(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── intersection observer for section entrance animations ────────────────────
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) setVisibleSections(prev => ({ ...prev, [e.target.id]: true }));
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  // ── unified scroll restoration ───────────────────────────────────────────────
  // This single function handles BOTH the website back button AND the browser
  // back button. It reads restoreScrollId from sessionStorage and scrolls to
  // the correct element. Called on mount (after data loads) and on pageshow.
  const doScrollRestore = useCallback(() => {
    // Prevent multiple simultaneous restoration attempts
    if (isRestoringRef.current) return;
    
    const savedId = sessionStorage.getItem('homeRestoreId') || sessionStorage.getItem('restoreScrollId');
    if (!savedId) return;

    isRestoringRef.current = true;

    // Get the section context to know which section to scroll to
    const sectionContext = sessionStorage.getItem('homeRestoreSection') || sessionStorage.getItem('restoreScrollSection');
    
    // Make all sections visible so the target card isn't hidden
    setVisibleSections({ properties: true, 'tanavi-highlights': true, banner: true, 'agri-banner': true });

    const isCategory = savedId.startsWith('cat-');
    const isChoice   = savedId.startsWith('choice-');
    const isSection  = savedId.startsWith('section-');

    const tryScroll = (attemptsLeft) => {
      if (attemptsLeft <= 0) {
        sessionStorage.removeItem('restoreScrollId');
        sessionStorage.removeItem('restoreScrollSection');
        sessionStorage.removeItem('homeRestoreId');
        sessionStorage.removeItem('homeRestoreSection');
        isRestoringRef.current = false;
        return;
      }

      let el = null;

      if (isSection) {
        // Navbar "Choice Properties" link — scroll to the section heading itself
        // savedId = 'section-choice-properties' → element id = 'choice-properties'
        const sectionId = savedId.replace('section-', '');
        el = document.getElementById(sectionId);
      } else if (isCategory) {
        // Category tiles have id attributes on their DOM elements
        el = document.getElementById(savedId);
      } else if (isChoice) {
        // Choice tiles have id attributes on their DOM elements
        el = document.getElementById(savedId);
      } else {
        // Property cards use React refs with composite key (section:id)
        // Build the composite key based on the section context
        const section = sectionContext || 'properties';
        const compositeKey = `${section}:${savedId}`;
        const ref = propertyRefs.current[compositeKey];
        el = ref?.current || null;
      }

      if (el) {
        // Simple smooth scroll to target element
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Clear sessionStorage shortly after scroll
        setTimeout(() => {
          sessionStorage.removeItem('restoreScrollId');
          sessionStorage.removeItem('restoreScrollSection');
          sessionStorage.removeItem('homeRestoreId');
          sessionStorage.removeItem('homeRestoreSection');
          isRestoringRef.current = false;
        }, 1000);
      } else {
        setTimeout(() => tryScroll(attemptsLeft - 1), 150);
      }
    };

    tryScroll(15); // retry up to 15 × 150ms = 2.25 s
  }, [propertyRefs]);

  // ── on mount: restore scroll after data is ready ─────────────────────────────
  useEffect(() => {
    if (loading) return; // wait for data
    const savedId = sessionStorage.getItem('homeRestoreId') || sessionStorage.getItem('restoreScrollId');
    if (!savedId) return;
    // Simple delay for scroll restoration
    const t = setTimeout(doScrollRestore, 500);
    return () => clearTimeout(t);
  }, [loading, doScrollRestore]);

  // ── pageshow: fires on EVERY page visit including browser back (bfcache) ─────
  // This is the only reliable way to catch the browser back button.
  useEffect(() => {
    const handlePageShow = () => {
      // Reset restoration flag to allow restoration on browser back
      isRestoringRef.current = false;
      
      const savedId = sessionStorage.getItem('homeRestoreId') || sessionStorage.getItem('restoreScrollId');
      if (!savedId) return;
      // Small delay so the page has finished restoring from bfcache
      setTimeout(doScrollRestore, 80);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [doScrollRestore]);

  // ── filtered list ────────────────────────────────────────────────────────────
  const filteredProperties = properties
    .filter(p =>
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.propertyCode && p.propertyCode.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .slice(0, 8);

  // ── skeleton — only on very first load, never on back-nav ───────────────────
  if (loading) return (
    <div>
      <div className="pt-12 min-h-[500px] bg-gray-200 animate-pulse" />
      <section className="py-4 md:py-6 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-10 bg-gray-200 rounded w-64 mb-6 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-200 animate-pulse">
                <div className="w-full h-48 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-6 bg-gray-200 rounded w-1/3" />
                  <div className="h-10 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <AnnouncementMarquee />
      <Hero searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      {/* Featured Properties */}
      <section
        id="properties"
        data-animate
        className={`py-4 md:py-6 bg-background transition-all duration-700
          ${visibleSections.properties ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6">Featured Properties</h2>
          <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
            {filteredProperties.map(property => (
              <div
                key={property._id}
                ref={getPropertyRef(property._id, 'properties')}
                className="flex-shrink-0 w-[calc(50%-8px)] snap-start md:w-auto"
              >
                <PropertyCard property={property} section="properties" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories — uses its own separate ref map, NOT propertyRefs */}
      <PropertyCategories getCategoryRef={getCategoryRef} />

      <TanaviHighlights getPropertyRef={getPropertyRef} />
      <ChoiceProperties getChoiceRef={getChoiceRef} />

      {/* Banner */}
      <section
        id="banner"
        data-animate
        className={`py-4 md:py-6 bg-background transition-all duration-700
          ${visibleSections.banner ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative h-64 md:h-80 rounded-lg overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-500">
            <img
              src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200"
              alt="Tanavi Properties Banner"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-secondary/90 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <h2 className="text-3xl md:text-5xl font-bold mb-4">Building Your Dreams</h2>
                <p className="text-lg md:text-xl">Experience luxury living with Tanavi Properties</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhyChoose />

      {/* Agri Banner */}
      <section
        id="agri-banner"
        data-animate
        className={`py-4 md:py-6 bg-background transition-all duration-700
          ${visibleSections['agri-banner'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative h-64 md:h-80 rounded-lg overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-500">
            <img
              src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200"
              alt="Agriculture Land"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-green-700/90 flex items-center justify-center">
              <div className="text-center text-white px-4 md:px-8">
                <h2 className="text-2xl md:text-4xl font-bold italic">
                  "Agriculture land sustains life — it feeds the world, nurtures generations, and grows in value forever."
                </h2>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <FAQ />
      <Essentials />
      <RegisterCTA />
    </div>
  );
};

export default Home;
